import type { Dispatch, SetStateAction } from "react";
import { useCallback, useRef, useState } from "react";
import { cancelAdhocDraft, type DocTypeConfig, draftNowRun, regenerateSection } from "../../api/client";
import type { ProgressEvent } from "../../types";
import type { RegenConfirmation, RegenTarget } from "../components/RegenOne";
import { estMinutes, sizeFromWordTarget } from "../selection";
import { sourceScopeNote } from "../services/sourceScopeNote";
import type { SpineSectionStatus } from "../sseAdapter";
import type { VariantCEvent } from "../state";
import type { SpineSection } from "./useVariantCRun";

export interface UseRegenFlowParams {
  focusedSectionId: string | null;
  docConfig: DocTypeConfig | null;
  sections: readonly SpineSection[];
  runId: string | null;
  runState: string;
  dispatch: Dispatch<VariantCEvent>;
  resolveSectionLabel: (id: string | null) => string | null;
  /** Eagerly flip the focused section's spine row to ``drafting`` on
   *  confirm so the writer doesn't see a stale ``drafted`` row while the
   *  backend boots the regen. The real SSE ``regen_started`` /
   *  ``module_start`` events overwrite this within a tick. */
  setSectionStatus: (sectionId: string, status: SpineSectionStatus) => void;
  /** Clear the terminal-run SSE gate so ``useVariantCRun`` re-subscribes
   *  to the original run's stream. After a reload of a completed run,
   *  this is true and would otherwise short-circuit the subscribe effect
   *  before the regen's events could land. */
  setTerminalRunStatus: (v: boolean) => void;
  /** Bump the subscription nonce so the SSE effect re-runs even though
   *  ``runId`` hasn't changed (we're staying on the same run_id, by
   *  design). Paired with the ``terminalRunStatus`` reset above. */
  setRunSubscriptionNonce: Dispatch<SetStateAction<number>>;
  /** Re-subscribe from "latest", never "history": a run opened from Drafts
   *  has no last-seen id, and a history subscribe replays the whole original
   *  run, painting every other section as drafting again. */
  setSubscriptionStartMode: (mode: "history" | "latest") => void;
  /** Seed the re-subscribe at the ``stream_cursor`` the regenerate endpoint
   *  returns, so the regen's own events replay from its first one. */
  seedStreamCursor: (id: string | null) => void;
  /** Close the Inspector drawer when regeneration is initiated. */
  setFocusedSectionId: Dispatch<SetStateAction<string | null>>;
}

/** Lifecycle of the one in-flight (or paused) single-section regen.
 *  ``pausing``/``stopping`` = the module-scoped stop has been sent and we are
 *  waiting for the server's ``regen_complete`` (outcome "cancelled"), by which
 *  point it has put the section's previous draft back. ``paused`` = stopped,
 *  but the writer can Resume, which restarts the regen with the same guidance
 *  (a regen cannot continue mid-draft — the agent starts the section over).
 *  ``finishing`` = "Draft now" was sent: the agent stops retrieving and drafts
 *  from what it has gathered; the regen then completes normally. */
export type RegenActivityPhase = "running" | "pausing" | "stopping" | "finishing" | "paused";

export interface RegenActivity {
  sectionId: string;
  sectionLabel: string | null;
  guidance: string | null;
  phase: RegenActivityPhase;
  /** Why the last Resume failed — the bar stays paused and shows it. */
  error?: string | null;
  /** Set by Resume until THIS attempt's ``regen_started`` arrives. A
   *  cancellation seen before then is the previous (stopped) attempt's late
   *  ``regen_complete``/``cancelled`` and must not tear the bar down. */
  awaitingStart?: boolean;
}

/** Wait before re-trying a Resume the server refused because the stopped
 *  attempt was still unwinding (409 / InvalidTransition). */
export const REGEN_RESUME_RETRY_DELAY_MS = 1500;

/** The server refuses a regenerate while the previous (stopped) attempt is
 *  still releasing the section: 409, or an InvalidTransition error body. */
function isTransientResumeConflict(err: unknown): boolean {
  const e = err as { status?: number; detail?: unknown; message?: unknown } | null;
  if (e?.status === 409) return true;
  const text = `${typeof e?.message === "string" ? e.message : ""} ${
    typeof e?.detail === "string" ? e.detail : JSON.stringify(e?.detail ?? "")
  }`;
  return /invalid[_ ]?transition/i.test(text);
}

export interface UseRegenFlowResult {
  regenTarget: RegenTarget | null;
  regenEnabled: boolean;
  handleInspectorRegenerate: () => void;
  /** Open the same regenerate dialog for a given section — used by the
   *  composer's "Regenerate this section" chip (cursor section). */
  openRegenFor: (sectionId: string) => void;
  handleRegenConfirm: (confirmation: RegenConfirmation) => Promise<void>;
  handleRegenCancel: () => void;
  /** The regen the control bar drives, or null when none is running/paused. */
  regenActivity: RegenActivity | null;
  pauseRegen: () => void;
  stopRegen: () => void;
  /** Finish the regen now from the evidence gathered so far. */
  draftNowRegen: () => void;
  resumeRegen: () => void;
  discardPausedRegen: () => void;
  /** Feed every run SSE event — settles the activity on the regen's end. */
  onRegenEvent: (event: ProgressEvent) => void;
}

export function useRegenFlow({
  focusedSectionId,
  docConfig,
  sections,
  runId,
  runState,
  dispatch,
  resolveSectionLabel,
  setSectionStatus,
  setTerminalRunStatus,
  setRunSubscriptionNonce,
  setSubscriptionStartMode,
  seedStreamCursor,
  setFocusedSectionId,
}: UseRegenFlowParams): UseRegenFlowResult {
  const [regenTarget, setRegenTarget] = useState<RegenTarget | null>(null);

  // "Regen" is only meaningful once a section has a draft to
  // regenerate; we gate the affordance on post-run state (the
  // drafted column is final). Pre-run / mid-run surfaces hide it.
  const regenEnabled = runState === "post-run";

  // The inspector "Regen" button opens a modal; writer confirms with
  // optional guidance. On confirm we dispatch the ``regen`` EventLog
  // entry and kick off a fresh single-section run via the same
  // `actuallyStartRun` primitive `Draft more sections` uses.
  const openRegenFor = useCallback(
    (sectionId: string) => {
      if (!docConfig) return;
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      // Derive the section's gate from docConfig (SpineSection doesn't
      // carry gate itself — it lives in the doctype config).
      const moduleId = Object.entries(docConfig.modules ?? {}).find(([, mod]) =>
        (mod.sections ?? []).includes(section.id),
      )?.[0];
      const gateIndex = (docConfig.gates ?? []).findIndex((g) => moduleId && (g.modules ?? []).includes(moduleId));
      const gateEntry = gateIndex >= 0 ? (docConfig.gates ?? [])[gateIndex] : null;
      // Honest time estimate: same word-target → size → minutes chain
      // LoomSelection uses.
      const skelRow = (docConfig.skeleton ?? []).find(([id]) => id === section.id);
      const wordTarget = skelRow ? skelRow[2] : 0;
      const est = estMinutes({
        n: section.id,
        t: section.title,
        gate: gateEntry?.name ?? "",
        size: sizeFromWordTarget(wordTarget),
      });
      setRegenTarget({
        sectionId: section.id,
        sectionNumber: section.number,
        sectionTitle: section.title,
        gateName: gateEntry?.name ?? "",
        gateDisplayName: gateEntry?.display_name ?? "",
        gatePaletteIndex: Math.max(0, gateIndex),
        estMinutes: est,
      });
    },
    [docConfig, sections],
  );

  const handleInspectorRegenerate = useCallback(() => {
    if (focusedSectionId) openRegenFor(focusedSectionId);
  }, [focusedSectionId, openRegenFor]);

  const [regenActivity, setRegenActivityState] = useState<RegenActivity | null>(null);
  // Mirror for the SSE handler, which must read the phase synchronously.
  const regenActivityRef = useRef<RegenActivity | null>(null);
  const setRegenActivity = useCallback((next: RegenActivity | null) => {
    regenActivityRef.current = next;
    setRegenActivityState(next);
  }, []);

  const startRegen = useCallback(
    async (confirmation: RegenConfirmation, resume: boolean) => {
      // Append the writer's regen intent to the EXISTING run's
      // transcript. No RUN_STARTED — we're not starting a new run; the
      // backend resets the section's SSOT and re-dispatches the gate
      // runner on the same run_id, emitting events into the SAME Redis
      // stream the original draft used.
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: confirmation.sectionId,
          sectionLabel: resolveSectionLabel(confirmation.sectionId),
          runId,
          payload: {
            kind: "regen",
            attempt: 1,
            outcome: "submitted",
            guidance: confirmation.guidance,
          },
        },
      });
      try {
        if (!runId) {
          // Regen is gated on post-run (regenEnabled); a post-run state
          // implies a run_id exists. Defensive bail keeps the modal from
          // wedging if the gate ever drifts.
          if (typeof console !== "undefined") {
            console.warn(
              "[useRegenFlow] handleRegenConfirm with null runId — skipping regenerateSection",
            );
          }
          return;
        }
        // Eager paint: flip the section's spine row to ``drafting`` on
        // confirm so the writer gets instant feedback instead of staring
        // at a stale ``drafted`` row while the backend spins up. The SSE
        // ``regen_started`` / ``module_start`` events will overwrite
        // within a tick with live sublines.
        setSectionStatus(confirmation.sectionId, "drafting");
        const attempt: RegenActivity = {
          sectionId: confirmation.sectionId,
          sectionLabel: resolveSectionLabel(confirmation.sectionId),
          guidance: confirmation.guidance ?? null,
          phase: "running",
          error: null,
          awaitingStart: resume,
        };
        setRegenActivity(attempt);
        const request = () =>
          regenerateSection(runId, confirmation.sectionId, {
            guidance: confirmation.guidance,
            source: "inspector",
          });
        let started: Awaited<ReturnType<typeof request>> | undefined;
        try {
          try {
            started = await request();
          } catch (err) {
            // A Resume right after the stop can race the server releasing the
            // stopped attempt — retry once after a beat.
            if (!resume || !isTransientResumeConflict(err)) throw err;
            console.warn("[useRegenFlow] regen resume conflicted; retrying once:", err);
            await new Promise((resolve) => setTimeout(resolve, REGEN_RESUME_RETRY_DELAY_MS));
            if (regenActivityRef.current?.sectionId !== confirmation.sectionId) throw err;
            started = await request();
          }
        } catch (err) {
          if (resume) {
            // Keep the bar (paused) so the writer can try Resume again or
            // Discard — dropping it left no way to control the section.
            const message = err instanceof Error ? err.message : String(err);
            setRegenActivity({ ...attempt, phase: "paused", awaitingStart: false, error: message });
          } else {
            setRegenActivity(null);
          }
          throw err;
        }
        // NOW re-subscribe FROM THE CURSOR (as the bulk resolve does): the
        // regen's events replay from its first one, and nothing of the
        // original run's history does. Clearing the terminal-run SSE gate
        // (set when a completed run is restored) and bumping the nonce
        // re-runs the subscribe effect on the same run_id.
        setSubscriptionStartMode("latest");
        seedStreamCursor(started?.stream_cursor ?? null);
        setTerminalRunStatus(false);
        setRunSubscriptionNonce((n) => n + 1);
        // "Use only X": say which sources the redraft reads, or that X matched nothing.
        const scopeNote = sourceScopeNote(started?.source_scope, resolveSectionLabel(confirmation.sectionId));
        if (scopeNote) {
          dispatch({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "asthra",
              sectionId: confirmation.sectionId,
              sectionLabel: resolveSectionLabel(confirmation.sectionId),
              runId,
              payload: { kind: "agent_note", text: scopeNote },
            },
          });
        }
        // Close the Inspector drawer once regeneration is initiated.
        setFocusedSectionId(null);
      } finally {
        setRegenTarget(null);
      }
    },
    [
      runId,
      resolveSectionLabel,
      dispatch,
      setSectionStatus,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setSubscriptionStartMode,
      seedStreamCursor,
      setFocusedSectionId,
      setRegenActivity,
    ],
  );

  const handleRegenConfirm = useCallback(
    (confirmation: RegenConfirmation) => startRegen(confirmation, false),
    [startRegen],
  );

  const handleRegenCancel = useCallback(() => {
    setRegenTarget(null);
  }, []);

  // Pause and Stop both arm the server's module-scoped stop (never
  // ``cancelRun`` — that would kill the whole run and flip it "cancelled").
  // The agent unwinds at its next tool round; the server restores the prior
  // draft and answers with ``regen_complete`` (outcome "cancelled").
  const requestStop = useCallback(
    (phase: "pausing" | "stopping") => {
      const current = regenActivityRef.current;
      if (!runId || !current || current.phase !== "running") return;
      setRegenActivity({ ...current, phase });
      void cancelAdhocDraft(runId, current.sectionId).catch((err) => {
        console.warn("[useRegenFlow] regen stop request failed:", err);
        const now = regenActivityRef.current;
        if (now && now.sectionId === current.sectionId) setRegenActivity({ ...now, phase: "running" });
      });
    },
    [runId, setRegenActivity],
  );
  const pauseRegen = useCallback(() => requestStop("pausing"), [requestStop]);
  const stopRegen = useCallback(() => requestStop("stopping"), [requestStop]);

  const draftNowRegen = useCallback(() => {
    const current = regenActivityRef.current;
    if (!runId || !current || current.phase !== "running") return;
    setRegenActivity({ ...current, phase: "finishing" });
    void draftNowRun(runId).catch((err) => {
      console.warn("[useRegenFlow] regen draft-now request failed:", err);
      const now = regenActivityRef.current;
      if (now && now.sectionId === current.sectionId) setRegenActivity({ ...now, phase: "running" });
    });
  }, [runId, setRegenActivity]);

  const resumeRegen = useCallback(() => {
    const current = regenActivityRef.current;
    if (!current || current.phase !== "paused") return;
    void startRegen({ sectionId: current.sectionId, guidance: current.guidance }, true).catch((err) => {
      console.warn("[useRegenFlow] regen resume failed:", err);
      setSectionStatus(current.sectionId, "drafted");
    });
  }, [startRegen, setSectionStatus]);

  const discardPausedRegen = useCallback(() => {
    if (regenActivityRef.current?.phase === "paused") setRegenActivity(null);
  }, [setRegenActivity]);

  const onRegenEvent = useCallback(
    (event: ProgressEvent) => {
      const current = regenActivityRef.current;
      if (!current || current.phase === "paused" || event.module_id !== current.sectionId) return;
      if (current.awaitingStart) {
        // Resumed: until this attempt's own ``regen_started`` lands, a
        // cancellation is the stopped attempt's — ignore it.
        if (event.event_type === "regen_started" || event.event_type === "module_start") {
          setRegenActivity({ ...current, awaitingStart: false });
          return;
        }
        const lateOutcome = (event.section_response as { outcome?: unknown } | undefined)?.outcome;
        if (
          event.event_type === "cancelled" ||
          (event.event_type === "regen_complete" && lateOutcome === "cancelled")
        ) {
          return;
        }
      }
      if (event.event_type === "regen_complete") {
        const outcome = (event.section_response as { outcome?: unknown } | undefined)?.outcome;
        // A pause that lost the race to a finished regen is just a finished regen.
        setRegenActivity(
          current.phase === "pausing" && outcome === "cancelled" ? { ...current, phase: "paused" } : null,
        );
      } else if (event.event_type === "error" || event.event_type === "cancelled") {
        setRegenActivity(null);
      }
    },
    [setRegenActivity],
  );

  return {
    regenTarget,
    regenEnabled,
    handleInspectorRegenerate,
    openRegenFor,
    handleRegenConfirm,
    handleRegenCancel,
    regenActivity,
    pauseRegen,
    stopRegen,
    draftNowRegen,
    resumeRegen,
    discardPausedRegen,
    onRegenEvent,
  };
}
